import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { useTranslation, Trans } from "react-i18next";
import { positionLabel } from "v2/constants/positions";
import { dateLocale } from "i18n";
import { PEER_COLORS, peerDirection } from "v2/utils/peerComparison";

// Same rules and neutral colours as the stat-card peer badge (TeamStatCard),
// so the list reads the same way as the selected-player detail.
const PEER_ICONS = { inLine: Minus, above: TrendingUp, below: TrendingDown };

const getPeerBadge = (diff, t) => {
  const direction = peerDirection(diff);
  if (!direction) return null;
  const percent = Math.abs(diff);
  return {
    ...PEER_COLORS[direction],
    Icon: PEER_ICONS[direction],
    label:
      direction === "inLine" ? t("team.playerListItem.avg") : `${percent}%`,
    headline:
      direction === "inLine"
        ? t("team.playerListItem.inLineWithPeers")
        : direction === "above"
        ? t("team.playerListItem.abovePeers", { percent })
        : t("team.playerListItem.belowPeers", { percent }),
  };
};

const PlayerListItem = React.forwardRef(
  ({ player, isSelected = false, onClick, hideStats = false }, ref) => {
    const { t } = useTranslation();
    const badgeRef = useRef(null);
    // { top, right } in viewport coords, or null when hidden
    const [tipPos, setTipPos] = useState(null);

    // Get initials from name
    const getInitials = (name) => {
      return name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
    };

    const peerBadge = getPeerBadge(player.loadVsPeers, t);

    const showTip = useCallback(() => {
      const rect = badgeRef.current?.getBoundingClientRect();
      if (rect) {
        setTipPos({
          top: rect.top - 8,
          right: window.innerWidth - rect.right,
        });
      }
    }, []);

    const hideTip = useCallback(() => setTipPos(null), []);

    // The tooltip is fixed-positioned, so any scroll would leave it floating
    // over stale coordinates — hide it instead.
    useEffect(() => {
      if (!tipPos) return;
      window.addEventListener("scroll", hideTip, true);
      return () => window.removeEventListener("scroll", hideTip, true);
    }, [tipPos, hideTip]);

    return (
      <div
        ref={ref}
        onClick={() => onClick?.(player)}
        className={`flex cursor-pointer items-center gap-3 border-b border-[#F3F4F6] px-4 py-3 transition duration-150 last:border-b-0 ${
          isSelected
            ? "border-l-2 border-l-[#E07A3A] bg-[#FEF7F3]"
            : "border-l-transparent border-l-2 hover:bg-[#FAFAFA]"
        }`}
      >
        {/* Avatar */}
        <div
          className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full ${
            isSelected ? "bg-[#E07A3A]" : "bg-[#F3F4F6]"
          }`}
        >
          <span
            className={`text-xs font-semibold ${
              isSelected ? "text-white" : "text-[#6B7280]"
            }`}
          >
            {getInitials(player.name)}
          </span>
        </div>

        {/* Player Info */}
        <div className="min-w-0 flex-1">
          <p className="truncate text-[13px] font-semibold text-[#111827]">
            {player.name}
          </p>
          <p className="truncate text-[11px] text-[#9CA3AF]">
            {positionLabel(player.position)}
          </p>
          {(player.consentStatus === "awaiting" ||
            player.consentStatus === "withdrawn") && (
            <span
              className={`mt-1 inline-block rounded px-1.5 py-0.5 text-[10px] font-semibold ${
                player.consentStatus === "awaiting"
                  ? "bg-[#FEF3C7] text-[#92400E]"
                  : "bg-[#FEF2F2] text-[#B91C1C]"
              }`}
            >
              {player.consentStatus === "awaiting"
                ? t("team.playerListItem.awaitingConsent")
                : player.deletionScheduledFor
                ? t("team.playerListItem.consentWithdrawnDeletion", {
                    date: new Date(
                      player.deletionScheduledFor
                    ).toLocaleDateString(dateLocale("en-GB"), {
                      day: "numeric",
                      month: "short",
                    }),
                  })
                : t("team.playerListItem.consentWithdrawn")}
            </span>
          )}
        </div>

        {/* Stats - hide when no session data */}
        {!hideStats && peerBadge && (
          <div
            ref={badgeRef}
            onMouseEnter={showTip}
            onMouseLeave={hideTip}
            className="flex flex-shrink-0 items-center gap-1 rounded px-1.5 py-0.5"
            style={{ backgroundColor: peerBadge.bg }}
          >
            <peerBadge.Icon size={10} style={{ color: peerBadge.text }} />
            <span
              className="text-[10px] font-medium"
              style={{ color: peerBadge.text }}
            >
              {peerBadge.label}
            </span>
          </div>
        )}

        {/* Tooltip — portalled + fixed so the list's scroll container can't clip it */}
        {tipPos &&
          peerBadge &&
          createPortal(
            <div
              className="pointer-events-none fixed z-50"
              style={{
                top: tipPos.top,
                right: tipPos.right,
                transform: "translateY(-100%)",
                animation: "sitTooltipIn 150ms ease-out",
              }}
            >
              <style>{`
                @keyframes sitTooltipIn {
                  from { opacity: 0; transform: translateY(calc(-100% + 4px)); }
                  to { opacity: 1; transform: translateY(-100%); }
                }
              `}</style>
              <div
                className="w-[210px] rounded-lg px-3 py-2.5 shadow-lg"
                style={{
                  backgroundColor: "#111827",
                  border: "1px solid rgba(255, 255, 255, 0.06)",
                }}
              >
                <div className="flex items-center gap-1.5">
                  <peerBadge.Icon
                    size={12}
                    style={{ color: peerBadge.tooltip }}
                  />
                  <span
                    className="text-xs font-semibold"
                    style={{ color: peerBadge.tooltip }}
                  >
                    {peerBadge.headline}
                  </span>
                </div>
                {player.peerAvgLoad != null && (
                  <p className="mt-1 text-[11px] text-[#9CA3AF]">
                    <Trans
                      i18nKey="team.playerListItem.peerAvgPerSession"
                      values={{
                        value: `${Math.round(
                          player.peerAvgLoad
                        ).toLocaleString()}g`,
                      }}
                      components={{
                        bold: <span className="font-medium text-white" />,
                      }}
                    />
                  </p>
                )}
                <p className="mt-1.5 border-t border-white/10 pt-1.5 text-[10px] leading-relaxed text-[#6B7280]">
                  {t("team.playerListItem.tooltipExplainer", {
                    count: player.peerSessions,
                  })}
                </p>
              </div>
              {/* Caret */}
              <div
                className="absolute right-4 h-2 w-2 rotate-45"
                style={{
                  backgroundColor: "#111827",
                  bottom: "-4px",
                  borderRight: "1px solid rgba(255, 255, 255, 0.06)",
                  borderBottom: "1px solid rgba(255, 255, 255, 0.06)",
                }}
              />
            </div>,
            document.body
          )}
      </div>
    );
  }
);

PlayerListItem.displayName = "PlayerListItem";

export default PlayerListItem;
